import React, { useEffect, useMemo, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function FoodSupplierMaster({ token, selectedPropertyId, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();
  const [form, setForm] = useState({
    supplier_id: '',
    company_name: '',
    contact_details: '',
    payment_terms: '',
    tax_number: '',
    approved_item_ids: [],
    is_active: true
  });

  const propertyId = String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': chainId,
    'x-property-id': propertyId,
    ...extra
  });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const resetForm = () => {
    setEditingId('');
    setForm({
      supplier_id: '',
      company_name: '',
      contact_details: '',
      payment_terms: '',
      tax_number: '',
      approved_item_ids: [],
      is_active: true
    });
  };

  const loadRows = async () => {
    if (!propertyId) return setRows([]);
    try {
      const res = await fetch('/api/v1/food/masters/suppliers', { headers: headers() });
      const { data, message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Failed to load suppliers');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading suppliers');
    }
  };

  const loadItems = async () => {
    if (!propertyId) return setItems([]);
    try {
      const res = await fetch('/api/v1/food/masters/items', { headers: headers() });
      const { data } = await parseResponse(res);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      setRows([]);
      return;
    }
    setError('');
    loadRows();
    loadItems();
  }, [token, selectedPropertyId, selectedProperty]);

  const itemMap = useMemo(() => {
    const map = new Map();
    items.forEach((i) => map.set(String(i.id), `${i.item_code || ''} ${i.item_name || ''}`.trim()));
    return map;
  }, [items]);

  const getApprovedLabel = (supplier) => {
    const ids = Array.isArray(supplier.approved_item_ids) ? supplier.approved_item_ids : [];
    return ids.map((id) => itemMap.get(String(id))).filter(Boolean).join(', ') || '-';
  };

  const save = async () => {
    setError('');
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    if (!String(form.supplier_id || '').trim()) return setError('Supplier ID is required');
    if (!String(form.company_name || '').trim()) return setError('Company Name is required');
    try {
      const payload = {
        supplier_id: String(form.supplier_id || '').trim(),
        company_name: String(form.company_name || '').trim(),
        contact_details: String(form.contact_details || '').trim(),
        payment_terms: String(form.payment_terms || '').trim(),
        tax_number: String(form.tax_number || '').trim(),
        approved_item_ids: form.approved_item_ids,
        is_active: form.is_active !== false
      };
      const url = editingId ? `/api/v1/food/masters/suppliers/${editingId}` : '/api/v1/food/masters/suppliers';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const { message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Save failed');
      resetForm();
      loadRows();
    } catch {
      setError('Network error while saving');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      supplier_id: row.supplier_id || '',
      company_name: row.company_name || '',
      contact_details: row.contact_details || '',
      payment_terms: row.payment_terms || '',
      tax_number: row.tax_number || '',
      approved_item_ids: Array.isArray(row.approved_item_ids) ? row.approved_item_ids.map(String) : [],
      is_active: row.is_active !== false
    });
    scrollToForm();
  };

  const removeRow = async (id) => {
    setError('');
    try {
      const res = await fetch(`/api/v1/food/masters/suppliers/${id}`, { method: 'DELETE', headers: headers() });
      const { message } = await parseResponse(res);
      if (!res.ok) return setError(message || 'Delete failed');
      loadRows();
    } catch {
      setError('Network error while deleting');
    }
  };

  const onApprovedChange = (e) => {
    const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    setForm({ ...form, approved_item_ids: selected });
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Inventory - Supplier Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Supplier' : 'Add Supplier'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
          <input value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} style={inputStyle} placeholder="Supplier ID" />
          <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} style={inputStyle} placeholder="Company Name" />
          <input value={form.contact_details} onChange={(e) => setForm({ ...form, contact_details: e.target.value })} style={inputStyle} placeholder="Contact Details" />
          <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} style={inputStyle} placeholder="Payment Terms" />
          <input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} style={inputStyle} placeholder="Tax Number" />
          <label style={{ gridColumn: '1 / -1' }}>
            <div style={{ marginBottom: '0.3rem', fontWeight: 'bold' }}>Approved Items List</div>
            <select multiple value={form.approved_item_ids} onChange={onApprovedChange} style={{ ...inputStyle, width: '100%', minHeight: '140px' }}>
              {items.map((i) => <option key={i.id} value={String(i.id)}>{`${i.item_code || ''} - ${i.item_name || ''}`}</option>)}
            </select>
          </label>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={save} className="btn-success" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>{editingId ? 'Update' : 'Add'}</button>
          <button onClick={resetForm} className="btn-muted" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Reset</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Suppliers</h3>
        {rows.length === 0 ? (
          <p>No suppliers found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Supplier ID</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Company Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Contact</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Payment Terms</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tax Number</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Approved Items</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id || r.supplier_id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.supplier_id}</td>
                  <td style={{ padding: '0.6rem' }}>{r.company_name}</td>
                  <td style={{ padding: '0.6rem' }}>{r.contact_details || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.payment_terms || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.tax_number || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{getApprovedLabel(r)}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => editRow(r)} className="btn-primary" style={{ padding: '0.4rem 0.7rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => removeRow(r.id)} className="btn-danger" style={{ padding: '0.4rem 0.7rem', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodSupplierMaster;
